import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, FlatList, StyleSheet, ActivityIndicator } from 'react-native';
import { useTranslation } from 'react-i18next';
import '../../i18n';
import { useDb } from '../../db/client';
import { getQuarantined, type OutboxEntry } from '../../db/outbox';
import { colors, fonts, spacing } from '../theme';
import { Heading } from '../shared/Typography';

/**
 * Quarentena visível (Etapa 15 — spec S05 §Estados na UI): itens de outbox
 * que falharam de forma NÃO recuperável (`status='quarantined'`, ver
 * markQuarantined em src/db/outbox.ts). Só visibilidade — sem ação de retry
 * manual nesta etapa, o critério da spec é "nunca travar a fila inteira por
 * um item" (já garantido pelo sync) mais tornar isso visível, não escondido.
 */
export function DebugScreen() {
  const { t } = useTranslation();
  const { db, ready } = useDb();
  const [items, setItems] = useState<OutboxEntry[] | null>(null);

  const load = useCallback(async () => {
    if (!db) return;
    setItems(await getQuarantined(db));
  }, [db]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <View style={styles.container}>
      <Heading style={styles.title}>{t('profile.debug.title')}</Heading>
      <Text style={styles.description}>{t('profile.debug.description')}</Text>

      {!ready || items === null ? (
        <ActivityIndicator color={colors.sageDeep} />
      ) : items.length === 0 ? (
        <Text style={styles.muted}>{t('profile.debug.empty')}</Text>
      ) : (
        <FlatList
          style={styles.list}
          data={items}
          keyExtractor={(item) => item.id}
          renderItem={({ item }) => (
            <View style={styles.item}>
              <Text style={styles.itemTable}>
                {item.tableName} · {item.operation}
              </Text>
              <Text style={styles.itemError}>{item.lastError}</Text>
              {item.lastAttemptAt && <Text style={styles.itemDate}>{item.lastAttemptAt.toLocaleString()}</Text>}
            </View>
          )}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, padding: spacing.md, backgroundColor: colors.sand },
  title: { textAlign: 'left', marginTop: spacing.md, marginBottom: spacing.sm },
  description: { fontFamily: fonts.body, color: colors.thread, marginBottom: spacing.md },
  muted: { fontFamily: fonts.body, color: colors.thread },
  list: { width: '100%' },
  item: { borderBottomWidth: 1, borderBottomColor: colors.line, paddingVertical: spacing.sm },
  itemTable: { fontFamily: fonts.bodyBold, fontSize: 14, color: colors.ink },
  itemError: { fontFamily: fonts.body, color: colors.clay, fontSize: 13, marginTop: 2 },
  itemDate: { fontFamily: fonts.body, color: colors.thread, fontSize: 12, marginTop: 2 },
});
